import { ResourceNode, type ResourceNodeType } from '@slimebound/core';
import {
  MeshBuilder,
  StandardMaterial,
  Color3,
  type Scene,
  type AbstractMesh,
  type ShadowGenerator
} from '@babylonjs/core';
import type { Inventory } from '@slimebound/core';
import type { NotificationLog } from '../ui/notification-log.js';

export interface WorldNodeInstance {
  node: ResourceNode;
  mesh: AbstractMesh;
  position: { x: number; y: number; z: number };
  radius: number;
}

export class ResourceNodeManager {
  private instances: WorldNodeInstance[] = [];

  public spawnResourceNodes(scene: Scene, shadowGenerator?: ShadowGenerator): void {
    const nodeDefs: Array<{
      type: ResourceNodeType;
      name: string;
      maxHealth: number;
      itemId: string;
      itemName: string;
      yieldQty: number;
      color: Color3;
      scale: { x: number; y: number; z: number };
      respawnSec: number;
      positions: Array<{ x: number; z: number }>;
    }> = [
      {
        type: 'herb_bush',
        name: 'Wild Herb Bush',
        maxHealth: 15,
        itemId: 'herb_vitality',
        itemName: 'Vitality Herb',
        yieldQty: 3,
        color: new Color3(0.18, 0.72, 0.28),
        scale: { x: 1.2, y: 1.0, z: 1.2 },
        respawnSec: 12,
        positions: [
          { x: -6, z: 6 },
          { x: 8, z: 8 },
          { x: -14, z: -4 }
        ]
      },
      {
        type: 'iron_vein',
        name: 'Iron Mineral Deposit',
        maxHealth: 30,
        itemId: 'iron_ore',
        itemName: 'Iron Ore',
        yieldQty: 4,
        color: new Color3(0.5, 0.55, 0.62),
        scale: { x: 1.6, y: 1.4, z: 1.6 },
        respawnSec: 18,
        positions: [
          { x: 12, z: -10 },
          { x: -10, z: 12 },
          { x: 6, z: -14 }
        ]
      },
      {
        type: 'crystal_geode',
        name: 'Mana Crystal Geode',
        maxHealth: 25,
        itemId: 'mana_crystal',
        itemName: 'Mana Crystal',
        yieldQty: 2,
        color: new Color3(0.8, 0.3, 0.95),
        scale: { x: 1.3, y: 1.8, z: 1.3 },
        respawnSec: 20,
        positions: [
          { x: -12, z: -12 },
          { x: 14, z: 12 }
        ]
      }
    ];

    let idCounter = 1;
    for (const def of nodeDefs) {
      const mat = new StandardMaterial(`nodeMat_${def.type}`, scene);
      mat.diffuseColor = def.color;
      mat.specularPower = 32;

      for (const pos of def.positions) {
        const id = `node_${idCounter++}`;
        const node = new ResourceNode({
          id,
          name: def.name,
          type: def.type,
          maxHealth: def.maxHealth,
          yieldItem: { itemId: def.itemId, name: def.itemName, quantity: def.yieldQty },
          respawnTimeSec: def.respawnSec
        });

        const mesh = MeshBuilder.CreateBox(
          id,
          { width: def.scale.x, height: def.scale.y, depth: def.scale.z },
          scene
        );
        mesh.position.set(pos.x, def.scale.y / 2, pos.z);
        mesh.material = mat;

        if (shadowGenerator) {
          shadowGenerator.addShadowCaster(mesh);
        }

        this.instances.push({
          node,
          mesh,
          position: { x: pos.x, y: def.scale.y / 2, z: pos.z },
          radius: Math.max(def.scale.x, def.scale.z) / 2
        });
      }
    }
  }

  public checkHarvestInteraction(
    slimePos: { x: number; y: number; z: number },
    slimeRadius: number,
    inventory: Inventory,
    notificationLog: NotificationLog,
    damage: number = 15
  ): boolean {
    for (const inst of this.instances) {
      if (inst.node.isDepleted) continue;

      const dx = slimePos.x - inst.position.x;
      const dz = slimePos.z - inst.position.z;
      const dist = Math.hypot(dx, dz);

      if (dist <= slimeRadius + inst.radius + 0.8) {
        const result = inst.node.damage(damage);
        if (result.hit) {
          if (result.destroyed && result.yield) {
            inst.mesh.setEnabled(false);
            inventory.addItem({
              itemId: result.yield.itemId,
              name: result.yield.name,
              quantity: result.yield.quantity
            });
            notificationLog.add(
              `⛏️ Harvested +${result.yield.quantity} ${result.yield.name}!`,
              'success',
              3.5
            );
          } else {
            notificationLog.add(
              `Harvesting ${inst.node.name}... (${inst.node.currentHealth}/${inst.node.maxHealth} HP)`,
              'info',
              1.5
            );
          }
          return true;
        }
      }
    }

    return false;
  }

  public update(dt: number): void {
    for (const inst of this.instances) {
      const respawned = inst.node.update(dt);
      if (respawned) {
        inst.mesh.setEnabled(true);
      }
    }
  }
}
